import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { convertLatexToMarkup } from "mathlive";
import "mathlive";
// 문제·보기·해설의 수식(convertLatexToMarkup)을 그리는 스타일과 글꼴. 없으면 위첨자·분수가 제자리에 안 선다
import "mathlive/static.css";
import "mathlive/fonts.css";

import apiClient from "../../../services/apiClient";
import secret from "../../../secret.json";
import RefreshIcon from "@mui/icons-material/Refresh";
import DescriptionIcon from "@mui/icons-material/DescriptionOutlined";
import PhotoCameraIcon from "@mui/icons-material/PhotoCameraOutlined";
import { CoinIcon, PhotoBonusBadge } from "../../reward/RewardIcons";
import { formatPoint } from "../../reward/pointStore";
import "../../reward/Store.css";
import ArrowButton from "../components/widgets/ArrowButton";
import useIsMobile from "../../../hooks/useIsMobile";
import { readSessionFromUrl } from "../../leveltest/session";
import "./MathPage.css";
import MathDrawingPad from "./MathDrawingPad";

// 지인에듀 수학 학습지 풀이 화면(2026-10-06). 서버: api/assignment/math_assignment.py
// - 학생은 1/2, sqrt(3), x^2 같은 글자를 치지 않는다. 입력칸은 수학 입력(MathLive)이고, 책에 쓰는 모양 그대로 보인다.
// - 입력칸 모양은 정답 모양으로 서버가 골라 준다(template): 숫자칸 / x = [ ] / ( [ ], [ ] ) / 식.
// - 정답은 제출 뒤에야 온다. 채점은 서버가 수학적으로 같은지 본다.
// - 연산(드릴)과 유형 문항을 색으로 나눈다.
// - 섹션 하나가 세트 하나(30문제)다. 세트마다 제출·채점하고, 채점 뒤에는 '틀린 문제' 탭에서 문제와 해설을 같이 본다.
// - 번호를 줄줄이 늘어놓지 않는다. 가운데 'n / 30' 을 누르면 번호판이 열려 원하는 문제로 뛴다.

const CIRCLED = ["①", "②", "③", "④", "⑤", "⑥", "⑦", "⑧"];
const CATEGORY_LABEL = { computation: "연산", type: "유형" };

// 풀이 사진(서버: api/assignment/math_work_photo.py). 처음 풀 때 세트를 내기 전에 묻는다. 필수면 올려야 넘어가고,
// 선택이면 건너뛸 수 있다. 선생님이 '다시' 라고 하면 채점 뒤 화면에서 다시 올린다
const WorkPhotoModal = ({ assignmentId, identity, isRequired, bonusPoints = 0, onDone, onSkip, onClose }) => {
  const inputRef = useRef(null);
  const [fileList, setFileList] = useState([]);
  const [isSending, setIsSending] = useState(false);
  const [message, setMessage] = useState("");
  const previewList = useMemo(
    () => fileList.map((file) => ({ file, url: file.type.startsWith("image/") ? URL.createObjectURL(file) : "" })),
    [fileList]
  );
  useEffect(() => () => previewList.forEach((one) => one.url && URL.revokeObjectURL(one.url)), [previewList]);

  const send = async () => {
    if (!fileList.length || isSending) return;
    setIsSending(true);
    setMessage("");
    const form = new FormData();
    form.append("assignment_id", assignmentId);
    form.append("student_id", identity.studentId);
    form.append("random_code", identity.randomCode);
    fileList.forEach((file) => form.append("files", file));
    try {
      // apiClient 는 기본이 JSON 이라 파일을 보낼 때는 multipart 를 꼭 적는다(안 적으면 파일이 빠진 채 간다)
      const response = await apiClient.post("/api/v1/assignment/math/work-photo", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onDone(response.data);
    } catch (error) {
      setMessage(error?.response?.data?.error || "올리지 못했어요. 다시 눌러 주세요.");
      setIsSending(false);
    }
  };

  return (
    <div className="store-sheet" onClick={onClose}>
      <div className="store-sheet__body work-photo-sheet" onClick={(event) => event.stopPropagation()}>
        <div className="store-invite__badge">
          <PhotoBonusBadge />
        </div>
        {bonusPoints > 0 ? (
          <>
            <div className="store-sheet__title store-invite__title">
              선생님께 <b>잘함</b> 받으면
            </div>
            <div className="store-price">
              <CoinIcon size="0.9em" /> +{formatPoint(bonusPoints)}
            </div>
          </>
        ) : (
          <div className="store-sheet__title store-invite__title">풀이 사진 올리기</div>
        )}
        {previewList.length > 0 && (
          <div className="work-photo__previews">
            {previewList.map((one, index) => (
              <div key={`${one.file.name}-${index}`} className="work-photo__preview">
                {one.url ? <img src={one.url} alt="" /> : <span>PDF</span>}
                <button type="button" onClick={() => setFileList((previous) => previous.filter((_, at) => at !== index))}>
                  ✕
                </button>
              </div>
            ))}
            <button type="button" className="work-photo__more" onClick={() => inputRef.current?.click()}>
              +
            </button>
          </div>
        )}
        <input
          ref={inputRef}
          type="file"
          accept="image/*,application/pdf"
          multiple
          hidden
          onChange={(event) => {
            setFileList((previous) => [...previous, ...Array.from(event.target.files)].slice(0, 20));
            event.target.value = "";
          }}
        />
        {message && <div className="store-message">{message}</div>}
        {fileList.length === 0 ? (
          <button type="button" className="store-button work-photo-sheet__pick" onClick={() => inputRef.current?.click()}>
            <PhotoCameraIcon /> 푼 종이 찍기
          </button>
        ) : (
          <button type="button" className="store-button" disabled={isSending} onClick={send}>
            {isSending ? "올리는 중" : "올리기"}
          </button>
        )}
        {!isRequired && onSkip ? (
          <button type="button" className="store-button store-button--ghost" onClick={onSkip}>
            다음에 할래요
          </button>
        ) : (
          <button type="button" className="store-button store-button--ghost" onClick={onClose}>
            닫기
          </button>
        )}
      </div>
    </div>
  );
};

// 틀린 문제 PDF 두 가지(서버: api/assignment/math_pdf.py). 카카오톡 안 브라우저에서도 열리게 그냥 링크로 연다
// 해설지는 전체를 채점한 모습(동그라미 · 엑스)이라 다 맞아도 연다. 오답 문제지는 틀린 문제가 있을 때만
const WrongPdfLinks = ({ assignmentId, identity, hasWrong = true }) => {
  if (!assignmentId || !identity) return null;
  const urlOf = (kind) =>
    `${secret.API_BASE_URL.replace(/\/+$/, "")}/api/v1/assignment/math/wrong-pdf?assignment_id=${assignmentId}&kind=${kind}` +
    `&student_id=${identity.studentId}&random_code=${identity.randomCode}`;
  return (
    <div className="math-page__pdf-row">
      {hasWrong && (
        <a className="math-page__pdf" href={urlOf("problem")} target="_blank" rel="noopener noreferrer">
          <RefreshIcon className="math-page__pdf-icon" />
          오답 문제지
        </a>
      )}
      <a className="math-page__pdf" href={urlOf("explain")} target="_blank" rel="noopener noreferrer">
        <DescriptionIcon className="math-page__pdf-icon" />
        해설지
      </a>
    </div>
  );
};
// 상세 해설 PDF(수학 사이트가 만들어 둔 것만 온다, 서명된 주소라 그대로 연다)와 문제은행 복습의 끝 표시.
// 틀린 문제 카드 아래에 붙는다(서버: api/assignment/math_assignment.py, math_bank_review.py)
const WrongProblemFooter = ({ detailPdfUrl, isReviewEnd }) => {
  if (!detailPdfUrl && !isReviewEnd) return null;
  return (
    <div className="math-page__wrong-footer">
      {detailPdfUrl && (
        <a className="math-page__pdf" href={detailPdfUrl} target="_blank" rel="noopener noreferrer">
          <DescriptionIcon className="math-page__pdf-icon" />
          상세 해설
        </a>
      )}
      {isReviewEnd && <span className="math-page__review-end">이 유형은 다 풀었어요</span>}
    </div>
  );
};

// 학습지 전체 상세 해설(다 만들어진 것만 서버가 준다)
const WorksheetDetailLink = ({ url }) =>
  url ? (
    <div className="math-page__pdf-row">
      <a className="math-page__pdf" href={url} target="_blank" rel="noopener noreferrer">
        <DescriptionIcon className="math-page__pdf-icon" />
        전체 상세 해설
      </a>
    </div>
  ) : null;

const COMPUTATION_PAGE_SIZE = 5;
// 한 화면의 답칸들(수식칸·글자칸). Enter 를 누르면 다음 칸으로, 마지막 칸이면 제출
const ANSWER_FIELD_SELECTOR = ".math-page__stage-body math-field:not([read-only]), .math-page__stage-body .math-page__text-field:not([disabled])";
const focusNextAnswerField = (current, onLast) => {
  const fieldList = Array.from(document.querySelectorAll(ANSWER_FIELD_SELECTOR));
  const next = fieldList[fieldList.indexOf(current) + 1];
  if (next) next.focus();
  else if (onLast) onLast();
};

// '$...$' 를 수식으로, 나머지는 글자로
// 수학 사이트 글에 그림 자리표 〈그림:67〉 가 섞여 오는 문항이 있다(2026-10-06 M-I011105). 그 문항의 image_url_list 를
// 자리표가 나온 순서대로 그 자리에 넣는다. 그림이 아직 안 왔으면 날 자리표 대신 '그림' 표시를 둔다
const FIGURE_PLACEHOLDER_PATTERN = /〈그림:\d+〉/g;
const countFigurePlaceholders = (text) => (String(text || "").match(FIGURE_PLACEHOLDER_PATTERN) || []).length;

// 수식($…$) 밖에 '|' 가 있는 줄은 표 줄이다. 수식 안의 '|' 는 절댓값
const isTableLine = (line) => line.replace(/\$[^$]+\$/g, "").includes("|");
const splitTableLine = (line) => {
  const cellList = [];
  let current = "";
  let isInMath = false;
  for (const character of line) {
    if (character === "$") isInMath = !isInMath;
    if (character === "|" && !isInMath) {
      cellList.push(current.trim());
      current = "";
      continue;
    }
    current += character;
  }
  cellList.push(current.trim());
  return cellList;
};

const renderMathText = (text, inlineImageList = []) => {
  let figureIndex = 0;
  const renderInline = (fragment) =>
    String(fragment)
      .split(/(\$[^$]+\$)/g)
      .map((part) => {
        if (part.startsWith("$") && part.endsWith("$") && part.length > 2) {
          try {
            return convertLatexToMarkup(part.slice(1, -1));
          } catch (error) {
            return part;
          }
        }
        return part
          .replace(/&/g, "&amp;")
          .replace(/</g, "&lt;")
          .replace(/>/g, "&gt;")
          .replace(/[ \t]*\|[ \t]*/g, " ")
          .replace(FIGURE_PLACEHOLDER_PATTERN, () => {
            const url = inlineImageList[figureIndex++];
            return url
              ? `<img class="math-page__inline-figure" src="${url.replace(/"/g, "&quot;")}" alt="">`
              : '<span class="math-page__figure-missing">그림</span>';
          });
      })
      .join("");
  // 이어진 표 줄('칸 | 칸')은 표로 그린다. 세로셈(13 × 53)이 한 줄씩 흩어지지 않고 자리가 맞는다(2026-10-12).
  // 나머지 줄바꿈은 띄어쓰기로 잇는다(원본 문서의 문장 중간 줄바꿈)
  const renderTable = (rowList) => {
    const columnCount = Math.max(...rowList.map((row) => row.length));
    const body = rowList
      .map((row) => `<tr>${[...row, ...Array(columnCount - row.length).fill("")].map((cell) => `<td>${renderInline(cell)}</td>`).join("")}</tr>`)
      .join("");
    return `<table class="math-page__grid">${body}</table>`;
  };
  return String(text || "")
    .split(/\n{2,}/)
    .map((paragraph) => {
      const pieceList = [];
      let tableRowList = [];
      let sentence = "";
      paragraph.split("\n").forEach((line) => {
        if (isTableLine(line)) {
          if (sentence.trim()) pieceList.push(renderInline(sentence.trim()));
          sentence = "";
          tableRowList.push(splitTableLine(line));
          return;
        }
        if (tableRowList.length) {
          pieceList.push(renderTable(tableRowList));
          tableRowList = [];
        }
        sentence = sentence ? `${sentence} ${line}` : line;
      });
      if (tableRowList.length) pieceList.push(renderTable(tableRowList));
      if (sentence.trim()) pieceList.push(renderInline(sentence.trim()));
      return pieceList.join("");
    })
    .join("<br><br>");
};

const MathText = ({ text, className, inlineImageList }) => (
  <span className={className} dangerouslySetInnerHTML={{ __html: renderMathText(text, inlineImageList) }} />
);

// 정답에 섞인 한글(또는, 중근, 평행사변형…)은 \text{} 로 감싸야 수식 글꼴로 붙어 버리지 않는다
const wrapKorean = (latex) =>
  String(latex ?? "")
    .replace(/\\text\{([^}]*)\}/g, "$1")
    .replace(/([가-힣][가-힣\s]*[가-힣]|[가-힣])/g, "\\text{$1}");

const MathLatex = ({ latex }) => (
  <span dangerouslySetInnerHTML={{ __html: latex ? convertLatexToMarkup(wrapKorean(latex)) : "" }} />
);

// MathLive 입력칸 하나. value 는 LaTeX
const MathField = ({ value, onChange, layout, disabled, placeholder, onEnter }) => {
  const fieldRef = useRef(null);
  const onEnterRef = useRef(onEnter);
  onEnterRef.current = onEnter;
  useEffect(() => {
    const field = fieldRef.current;
    if (!field) return undefined;
    // 데스크톱에서 Enter 로 제출(2026-10-06 대표)
    const handleKeyDown = (event) => {
      if (event.key !== "Enter") return;
      event.preventDefault();
      // 같은 화면에 다음 답칸이 있으면 그리로(다섯 문제 화면, 좌표·근이 여러 칸인 답). 없으면 제출
      const fieldList = Array.from(document.querySelectorAll(ANSWER_FIELD_SELECTOR));
      const next = fieldList[fieldList.indexOf(field) + 1];
      if (next) next.focus();
      else if (onEnterRef.current) onEnterRef.current();
    };
    field.addEventListener("keydown", handleKeyDown);
    return () => field.removeEventListener("keydown", handleKeyDown);
  }, []);
  useEffect(() => {
    const field = fieldRef.current;
    if (!field) return undefined;
    field.mathVirtualKeyboardPolicy = "auto";
    if (field.value !== (value || "")) field.value = value || "";
    const handleInput = () => onChange(field.value);
    field.addEventListener("input", handleInput);
    return () => field.removeEventListener("input", handleInput);
  }, [value, onChange]);
  useEffect(() => {
    if (window.mathVirtualKeyboard) {
      window.mathVirtualKeyboard.layouts = layout === "number" ? ["numeric"] : ["numeric", "symbols", "alphabetic"];
    }
  }, [layout]);
  return (
    <math-field
      ref={fieldRef}
      class={`math-page__field${layout === "number" ? " math-page__field--short" : ""}${disabled ? " math-page__field--disabled" : ""}`}
      read-only={disabled ? "" : undefined}
      placeholder={placeholder || ""}
      onFocus={() => {
        if (window.mathVirtualKeyboard) {
          window.mathVirtualKeyboard.layouts = layout === "number" ? ["numeric"] : ["numeric", "symbols", "alphabetic"];
        }
      }}
    />
  );
};

const splitCoordinate = (value) => {
  const inner = String(value || "").replace(/^\\left\(|^\(/, "").replace(/\\right\)$|\)$/, "");
  const commaIndex = inner.indexOf(",");
  return commaIndex < 0 ? [inner, ""] : [inner.slice(0, commaIndex), inner.slice(commaIndex + 1)];
};

// 한글 낱말 답(정답이 '평행사변형' 같은 것)이나 (가)(나) 칸을 받는 글자칸. 수식칸은 한글을 못 친다
const TextField = ({ value, onChange, disabled, onEnter }) => (
  <input
    type="text"
    className="math-page__text-field"
    value={value}
    disabled={disabled}
    onChange={(event) => onChange(event.target.value)}
    onKeyDown={(event) => {
      if (event.key !== "Enter" || event.nativeEvent.isComposing) return;
      event.preventDefault();
      focusNextAnswerField(event.currentTarget, onEnter);
    }}
  />
);

const readPartMap = (value) => {
  try {
    return value ? JSON.parse(value) : {};
  } catch (error) {
    return {};
  }
};

// 숫자 하나 칸(대분수의 자연수·분자·분모). 수식칸의 분수 버튼은 앞 숫자를 분자로 가져가서 대분수를 못 만든다
const NumberBox = ({ value, onChange, disabled, onEnter, label }) => (
  <input
    type="text"
    inputMode="numeric"
    aria-label={label}
    className="math-page__text-field math-page__number-box"
    value={value}
    disabled={disabled}
    onChange={(event) => onChange(event.target.value.replace(/[^\d-]/g, ""))}
    onKeyDown={(event) => {
      if (event.key !== "Enter" || event.nativeEvent.isComposing) return;
      event.preventDefault();
      focusNextAnswerField(event.currentTarget, onEnter);
    }}
  />
);

// 정답이 여럿인 객관식·기호 답은 여러 개 고른다. 값은 배열(객관식) 또는 "㉡, ㉢"(기호)
const toggledList = (list, item) => (list.includes(item) ? list.filter((one) => one !== item) : [...list, item]);

const readMixed = (value) => {
  const matched = String(value || "").match(/^(-?\d*)\\frac\{(\d*)\}\{(\d*)\}$/);
  return matched ? { whole: matched[1], numerator: matched[2], denominator: matched[3] } : { whole: "", numerator: "", denominator: "" };
};

// 대분수 하나: [자연수] [분자/분모]. 셋 다 비면 빈 답
const MixedNumberInput = ({ value, onChange, disabled, onEnter }) => {
  const mixed = readMixed(value);
  const update = (key, next) => {
    const nextMixed = { ...mixed, [key]: next };
    const isEmpty = !nextMixed.whole && !nextMixed.numerator && !nextMixed.denominator;
    onChange(isEmpty ? "" : `${nextMixed.whole}\\frac{${nextMixed.numerator}}{${nextMixed.denominator}}`);
  };
  return (
    <>
      <NumberBox label="자연수" value={mixed.whole} onChange={(next) => update("whole", next)} disabled={disabled} onEnter={onEnter} />
      <span className="math-page__fraction">
        <NumberBox label="분자" value={mixed.numerator} onChange={(next) => update("numerator", next)} disabled={disabled} onEnter={onEnter} />
        <span className="math-page__fraction-line" />
        <NumberBox label="분모" value={mixed.denominator} onChange={(next) => update("denominator", next)} disabled={disabled} onEnter={onEnter} />
      </span>
    </>
  );
};

const ShortAnswer = ({ problem, value, onChange, disabled, onEnter }) => {
  const template = problem.template || { kind: "expression" };
  const unit = template.unit ? <span className="math-page__unit">{template.unit}</span> : null;
  if (template.kind === "mixed") {
    return (
      <div className="math-page__answer-row">
        <MixedNumberInput value={value} onChange={onChange} disabled={disabled} onEnter={onEnter} />
        {unit}
      </div>
    );
  }
  if (template.kind === "list") {
    // 값이 여럿인 답(25, 18.3, 30): 값마다 칸 하나. 쉼표로 이어 보낸다(서버가 칸 순서대로 맞춰 본다)
    const itemList = String(value || "").split(",");
    const update = (itemIndex, next) => {
      const nextList = Array.from({ length: template.count }, (_, index) => (index === itemIndex ? next : itemList[index] || ""));
      onChange(nextList.some((one) => one.trim()) ? nextList.join(",") : "");
    };
    return (
      <div className="math-page__answer-row math-page__answer-row--list">
        {/* (위에서부터) 같은 읽는 순서 머리말 */}
        {template.label && <span className="math-page__list-label">{template.label}</span>}
        {Array.from({ length: template.count }, (_, itemIndex) => (
          <span key={itemIndex} className="math-page__list-item">
            {/* 칸마다 라벨(몫 · 나머지)이 있으면 쉼표 없이 라벨로 가른다 */}
            {itemIndex > 0 && !template.item_label_list && <span className="math-page__paren">{template.separator || ","}</span>}
            {template.item_label_list?.[itemIndex] && (
              <span className="math-page__list-label">{template.item_label_list[itemIndex]}</span>
            )}
            {template.item_kind === "mixed" ? (
              <MixedNumberInput value={itemList[itemIndex] || ""} onChange={(next) => update(itemIndex, next)} disabled={disabled} onEnter={onEnter} />
            ) : (
              <MathField onEnter={onEnter} value={itemList[itemIndex] || ""} onChange={(next) => update(itemIndex, next)} layout="number" disabled={disabled} />
            )}
            {unit}
          </span>
        ))}
      </div>
    );
  }
  if (template.kind === "units") {
    // 복합 단위(8 cm 7 mm, 3시 20분 15초): 단위마다 숫자 칸. 값만 쉼표로 이어 보낸다
    const itemList = String(value || "").split(",");
    const update = (itemIndex, next) => {
      const nextList = template.unit_list.map((_, index) => (index === itemIndex ? next : itemList[index] || ""));
      onChange(nextList.some((one) => one.trim()) ? nextList.join(",") : "");
    };
    return (
      <div className="math-page__answer-row math-page__answer-row--list">
        {template.unit_list.map((unitName, itemIndex) => (
          <span key={itemIndex} className="math-page__list-item">
            <MathField onEnter={onEnter} value={itemList[itemIndex] || ""} onChange={(next) => update(itemIndex, next)} layout="number" disabled={disabled} />
            <span className="math-page__unit">{unitName}</span>
          </span>
        ))}
      </div>
    );
  }
  if (template.kind === "mark") {
    // ○ 표 하기('( ) ( ○ ) ( )'): 괄호 자리 중 ○ 할 곳을 누른다. 고른 자리 번호를 보낸다
    const pickedList = String(value || "").split(",").map((one) => one.trim()).filter(Boolean);
    const pick = (slot) => {
      const nextList = template.multiple ? toggledList(pickedList, slot) : [slot];
      onChange(nextList.sort().join(","));
    };
    return (
      <div className="math-page__answer-row math-page__symbols">
        {Array.from({ length: template.count }, (_, slotIndex) => {
          const slot = String(slotIndex + 1);
          const isPicked = pickedList.includes(slot);
          return (
            <button
              key={slot}
              type="button"
              disabled={disabled}
              aria-pressed={isPicked}
              aria-label={`${slotIndex + 1}번째 괄호`}
              className={`math-page__symbol math-page__mark${isPicked ? " math-page__symbol--picked" : ""}`}
              onClick={() => pick(slot)}
            >
              ( {isPicked ? "○" : "\u00a0\u00a0"} )
            </button>
          );
        })}
      </div>
    );
  }
  if (template.kind === "text_list") {
    // 낱말 여러 개(변, 꼭짓점): 낱말마다 글자칸. ', ' 로 이어 보내고 AI 가 매긴다
    const itemList = String(value || "").split(", ");
    const update = (itemIndex, next) => {
      const nextList = Array.from({ length: template.count }, (_, index) => (index === itemIndex ? next : itemList[index] || ""));
      onChange(nextList.some((one) => one.trim()) ? nextList.join(", ") : "");
    };
    return (
      <div className="math-page__parts">
        {Array.from({ length: template.count }, (_, itemIndex) => (
          <TextField key={itemIndex} value={itemList[itemIndex] || ""} onChange={(next) => update(itemIndex, next)} disabled={disabled} onEnter={onEnter} />
        ))}
      </div>
    );
  }
  if (template.kind === "symbol") {
    // 기호를 쓰는 답(㉠㉡, ㄱㄴ, ①②, > = <). 버튼으로 고른다. 여러 개면 문제에 나온 순서대로 이어 보낸다
    const pickedList = String(value || "").split(",").map((one) => one.trim()).filter(Boolean);
    const pick = (symbol) => {
      const nextList = template.multiple ? toggledList(pickedList, symbol) : [symbol];
      // '순서대로 기호를 쓰는' 문항(template.ordered)은 누른 차례가 곧 답이다. 나머지는 문제에 나온 순서로 보낸다
      onChange((template.ordered ? nextList : template.option_list.filter((one) => nextList.includes(one))).join(", "));
    };
    return (
      <div className="math-page__answer-row math-page__symbols">
        {template.option_list.map((symbol) => (
          <button
            key={symbol}
            type="button"
            disabled={disabled}
            aria-pressed={pickedList.includes(symbol)}
            className={`math-page__symbol${pickedList.includes(symbol) ? " math-page__symbol--picked" : ""}`}
            onClick={() => pick(symbol)}
          >
            {symbol}
            {template.ordered && pickedList.includes(symbol) && (
              <span className="math-page__symbol-order">{pickedList.indexOf(symbol) + 1}</span>
            )}
          </button>
        ))}
        {template.multiple && (
          <span className="math-page__symbol-hint">{template.ordered ? "차례대로 누르기" : "모두 고르기"}</span>
        )}
      </div>
    );
  }
  if (template.kind === "ratio") {
    // 비: [ ] : [ ]. 숫자 칸 키보드에는 쌍점이 없다
    const [left, right] = String(value || "").split(":");
    const update = (nextLeft, nextRight) => onChange(nextLeft || nextRight ? `${nextLeft || ""}:${nextRight || ""}` : "");
    return (
      <div className="math-page__answer-row">
        <MathField onEnter={onEnter} value={left || ""} onChange={(next) => update(next, right)} layout="number" disabled={disabled} />
        <span className="math-page__paren">:</span>
        <MathField onEnter={onEnter} value={right || ""} onChange={(next) => update(left, next)} layout="number" disabled={disabled} />
      </div>
    );
  }
  if (template.kind === "text") {
    return (
      <div className="math-page__answer-row">
        <TextField value={value} onChange={onChange} disabled={disabled} onEnter={onEnter} />
      </div>
    );
  }
  if (template.kind === "parts") {
    // 칸별 답을 {"가": "…"} 로 모은다. 다 비면 빈 답
    const partMap = readPartMap(value);
    const update = (label, text) => {
      const next = { ...partMap, [label]: text };
      onChange(Object.values(next).some((one) => String(one).trim()) ? JSON.stringify(next) : "");
    };
    return (
      <div className="math-page__parts">
        {template.label_list.map((label, partIndex) => {
          // 칸마다 그 칸 정답 모양의 입력칸(숫자·기호·글자). 서버가 part_template_list 로 준다
          const partTemplate = template.part_template_list?.[partIndex];
          return (
            <div key={label} className="math-page__part">
              <span className="math-page__part-label">({label})</span>
              {partTemplate && partTemplate.kind !== "text" ? (
                <ShortAnswer
                  problem={{ template: partTemplate }}
                  value={partMap[label] || ""}
                  onChange={(text) => update(label, text)}
                  disabled={disabled}
                  onEnter={onEnter}
                />
              ) : (
                <TextField value={partMap[label] || ""} onChange={(text) => update(label, text)} disabled={disabled} onEnter={onEnter} />
              )}
            </div>
          );
        })}
      </div>
    );
  }
  if (template.kind === "roots") {
    // x = [ ] 또는 x = [ ]. 값만 쉼표로 이어 보낸다(서버가 순서와 상관없이 짝짓는다)
    const rootList = String(value || "").split(",");
    const update = (rootIndex, next) => {
      const nextList = Array.from({ length: template.count }, (_, index) => (index === rootIndex ? next : rootList[index] || ""));
      onChange(nextList.some((one) => one.trim()) ? nextList.join(",") : "");
    };
    return (
      <div className="math-page__answer-row math-page__answer-row--roots">
        {Array.from({ length: template.count }, (_, rootIndex) => (
          <span key={rootIndex} className="math-page__root">
            {rootIndex > 0 && <span className="math-page__or">또는</span>}
            <MathLatex latex={`${template.prefix}=`} />
            <MathField onEnter={onEnter} value={rootList[rootIndex] || ""} onChange={(next) => update(rootIndex, next)} layout="number" disabled={disabled} />
          </span>
        ))}
      </div>
    );
  }
  if (template.kind === "assignment") {
    return (
      <div className="math-page__answer-row">
        <MathLatex latex={`${template.prefix}=`} />
        <MathField onEnter={onEnter} value={value} onChange={onChange} layout="number" disabled={disabled} />
        {unit}
      </div>
    );
  }
  if (template.kind === "coordinate") {
    const [left, right] = splitCoordinate(value);
    const update = (nextLeft, nextRight) => onChange(`(${nextLeft},${nextRight})`);
    return (
      <div className="math-page__answer-row">
        <span className="math-page__paren">(</span>
        <MathField onEnter={onEnter} value={left} onChange={(next) => update(next, right)} layout="number" disabled={disabled} />
        <span className="math-page__paren">,</span>
        <MathField onEnter={onEnter} value={right} onChange={(next) => update(left, next)} layout="number" disabled={disabled} />
        <span className="math-page__paren">)</span>
      </div>
    );
  }
  return (
    <div className="math-page__answer-row">
      <MathField onEnter={onEnter}
        value={value}
        onChange={onChange}
        layout={template.kind === "number" ? "number" : "expression"}
        disabled={disabled}
      />
      {unit}
    </div>
  );
};

const isAnswered = (answer) =>
  answer !== undefined && answer !== null && answer !== "" && !(Array.isArray(answer) && answer.length === 0);

// 정답 글자. 글자로 적은 대분수 '11 2/3' 은 수식으로 그리면 띄어쓰기가 사라져 '112/3' 으로 보여서 대분수 모양으로 바꾼다
const answerTextOf = (answerKey) =>
  (Array.isArray(answerKey?.answer) ? answerKey.answer.join(", ") : String(answerKey?.answer ?? "")).replace(
    /(^|[^\d.}])(-?\d+)\s+(\d+)\s*\/\s*(\d+)(?![\d.])/g,
    "$1$2\\frac{$3}{$4}"
  );

// 문제 하나(태그·문제·입력·해설). 풀이 화면과 틀린 문제 목록이 같이 쓴다
const ProblemCard = ({ problem, number, value, onChange, isFinished, graded, answerKey, footer = null, onEnter, onPick, isCompact = false }) => {
  // 그림 자리표가 있고 그림이 그만큼 왔으면 글 속 제자리에 넣는다. 나머지 그림은 늘 하던 대로 글 아래
  // 그리기 문항은 첫 그림 위에 그린다. 글 속 자리에 넣지 않는다
  const placeholderCount = problem.is_drawing ? 0 : countFigurePlaceholders(problem.question);
  const inlineImageList =
    placeholderCount && (problem.image_url_list || []).length >= placeholderCount
      ? problem.image_url_list.slice(0, placeholderCount)
      : [];
  return (
  <div
    className={`math-page__card${isCompact ? " math-page__card--compact" : ""}${
      isFinished && graded?.is_correct === true ? " math-page__card--right" : ""
    }${isFinished && graded?.is_correct === false ? " math-page__card--wrong" : ""}`}
  >
    <div className="math-page__tags">
      <span className={`math-page__category math-page__category--${problem.category}`}>
        {CATEGORY_LABEL[problem.category] || "문제"}
      </span>
      {problem.type_label && <span className="math-page__tag">{problem.type_label}</span>}
      {isFinished && graded?.is_correct === true && <span className="math-page__result math-page__result--right">정답</span>}
      {isFinished && graded?.is_correct === false && <span className="math-page__result math-page__result--wrong">오답</span>}
      {isFinished && problem.is_drawing && graded?.is_correct == null && (
        <span className="math-page__result math-page__result--waiting">선생님 채점</span>
      )}
    </div>
    <div className="math-page__question">
      <span className="math-page__no">{number}.</span>
      <MathText text={problem.question} inlineImageList={inlineImageList} />
    </div>
    {(problem.image_url_list || []).slice(problem.is_drawing ? 1 : inlineImageList.length).map((url) => (
      <img key={url} className="math-page__image" src={url} alt="" />
    ))}
    {problem.is_drawing && (
      <MathDrawingPad imageUrl={(problem.image_url_list || [])[0]} value={value} onChange={onChange} disabled={isFinished} />
    )}

    {problem.answer_type === "choice" && !problem.is_drawing && (
      <div className="math-page__choices">
        {problem.choice_list.map((choice, choiceIndex) => {
          const choiceNumber = String(choiceIndex + 1);
          const pickedList = Array.isArray(value) ? value.map(String) : [String(value)];
          const isPicked = pickedList.includes(choiceNumber);
          const isAnswer =
            isFinished &&
            (Array.isArray(answerKey?.answer) ? answerKey.answer.map(String) : [String(answerKey?.answer)]).includes(choiceNumber);
          return (
            <button
              key={choiceNumber}
              type="button"
              disabled={isFinished}
              className={`math-page__choice${isPicked ? " math-page__choice--picked" : ""}${
                isAnswer ? " math-page__choice--answer" : ""
              }${isFinished && isPicked && !isAnswer ? " math-page__choice--wrong" : ""}`}
              onClick={() => {
                if (problem.multiple) {
                  // 정답이 여럿인 문제는 여러 개 고른다. 다음 문제로 저절로 넘어가지 않는다
                  onChange(toggledList(Array.isArray(value) ? value.map(String) : [], choiceNumber));
                  return;
                }
                onChange(choiceNumber);
                if (onPick) onPick(choiceNumber);
              }}
            >
              <span className="math-page__circled">{CIRCLED[choiceIndex]}</span>
              <MathText text={choice} />
            </button>
          );
        })}
      </div>
    )}
    {problem.answer_type === "short" && !problem.is_drawing && (
      <ShortAnswer problem={problem} value={value} onChange={onChange} disabled={isFinished} onEnter={onEnter} />
    )}
    {problem.answer_type === "essay" && !problem.is_drawing && (
      <textarea
        className="math-page__essay"
        value={value}
        disabled={isFinished}
        onChange={(event) => onChange(event.target.value)}
      />
    )}

    {isFinished && answerKey && (
      <div className="math-page__explain">
        {problem.answer_type !== "choice" && answerKey.answer !== undefined && (
          <div className="math-page__explain-answer">
            정답 <MathLatex latex={answerTextOf(answerKey)} />
          </div>
        )}
        {answerKey.explanation && <MathText text={answerKey.explanation} className="math-page__explain-text" />}
        {answerKey.explanation_image_url && <img className="math-page__image" src={answerKey.explanation_image_url} alt="" />}
      </div>
    )}
    {footer}
  </div>
  );
};

// 남은 시간 'mm:ss'
const clockText = (seconds) => `${String(Math.floor(seconds / 60)).padStart(2, "0")}:${String(seconds % 60).padStart(2, "0")}`;

// 레벨테스트 시험 시간(2026-10-21, api/assignment/math_level_test.py). 시작 시각은 서버가 쥐고 있어 새로 고쳐도 줄지 않는다.
// 서버가 준 남은 초를 받은 순간의 이 기기 시계에 얹어 센다. 다 되면 onTimeUp 을 한 번 부른다
const useLevelTestClock = (data, onTimeUp) => {
  const [remainingSeconds, setRemainingSeconds] = useState(null);
  const onTimeUpRef = useRef(onTimeUp);
  onTimeUpRef.current = onTimeUp;
  const isRunning = Boolean(data && data.time_limit_minutes && data.remaining_seconds != null && !data.is_finished);
  useEffect(() => {
    if (!isRunning) {
      setRemainingSeconds(null);
      return undefined;
    }
    const endAt = Date.now() + data.remaining_seconds * 1000;
    let isFired = false;
    const tick = () => {
      const left = Math.max(0, Math.round((endAt - Date.now()) / 1000));
      setRemainingSeconds(left);
      if (left === 0 && !isFired) {
        isFired = true;
        onTimeUpRef.current();
      }
    };
    tick();
    const timer = window.setInterval(tick, 1000);
    return () => window.clearInterval(timer);
  }, [isRunning, data?.remaining_seconds, data?.section_id]);
  return remainingSeconds;
};

// 문항별 시간 레벨테스트(2026-10-22, api/assignment/math_level_test.py). ECHO 영어 월말 시험 화면(test-player.html)과
// 같은 모양: 머리줄 · 줄어드는 시간 막대 · 문항 카드 · 아래 '다음 문항'.
// - 지금 몇 번 문항인지와 그 문항의 남은 시간은 서버가 정한다(current_problem_index, problem_remaining_seconds).
//   새로 고쳐도, 기기를 바꿔도 이어서 줄어든다.
// - 시간이 다 되면 쓴 답을 보내고 다음 문항으로 간다. 지나간 문항으로는 돌아가지 못한다(서버도 끝난 문항 답을 받지 않는다).
// - 마지막 문항이 끝나면 서버가 채점해 끝낸 결과를 돌려준다.
// - 요청은 화면을 덮지 않는다(isSilent). 덮개가 뜨면 시계가 가는 동안 문제가 가려진다.
const LEVEL_TEST_HURRY_SECONDS = 20;
const LEVEL_TEST_AUTOSAVE_DELAY_MS = 2500;
const testClockText = (seconds) => `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, "0")}`;

const MathLevelTestRunner = ({ data, valueById, setValue, identity, onData }) => {
  const problemList = data.problem_list;
  const currentIndex = data.current_problem_index;
  const problem = currentIndex != null ? problemList[currentIndex] : null;
  const [remainingSeconds, setRemainingSeconds] = useState(data.problem_remaining_seconds ?? 0);
  const [isSending, setIsSending] = useState(false);
  const valueByIdRef = useRef(valueById);
  valueByIdRef.current = valueById;
  const isSendingRef = useRef(false);
  const currentIndexRef = useRef(currentIndex);
  currentIndexRef.current = currentIndex;

  const responseListOf = useCallback(
    () => problemList.map((one) => ({ problem_id: one.problem_id, value: valueByIdRef.current[one.problem_id] ?? "" })),
    [problemList]
  );
  const reload = useCallback(() => {
    apiClient
      .get("/api/v1/assignment/math/section", {
        params: { section_id: data.section_id, student_id: identity.studentId, random_code: identity.randomCode },
        isSilent: true,
      })
      .then((response) => onData(response.data))
      .catch(() => {});
  }, [data.section_id, identity, onData]);

  // 이 문항을 끝낸다(다음 문항 · 시간 다 됨). 서버가 다음 문항이나 채점 결과를 돌려준다
  const endProblem = useCallback(async () => {
    if (isSendingRef.current || !problem) return;
    isSendingRef.current = true;
    setIsSending(true);
    try {
      const response = await apiClient.post("/api/v1/assignment/math/section/submit", {
        section_id: data.section_id,
        student_id: identity.studentId,
        random_code: identity.randomCode,
        response_list: responseListOf(),
        end_problem_id: problem.problem_id,
      }, { isSilent: true });
      onData(response.data);
      window.scrollTo(0, 0);
    } catch (caught) {
      // 저장이 실패해도 시간표는 서버에 있다. 서버가 보는 지금 문항으로 다시 맞춘다
      reload();
    } finally {
      isSendingRef.current = false;
      setIsSending(false);
    }
  }, [data.section_id, identity, problem, responseListOf, onData, reload]);
  const endProblemRef = useRef(endProblem);
  endProblemRef.current = endProblem;

  // 이 문항의 시계. 서버가 준 남은 초를 받은 순간의 이 기기 시계에 얹어 센다
  useEffect(() => {
    if (currentIndex == null) return undefined;
    const endAt = Date.now() + (data.problem_remaining_seconds ?? 0) * 1000;
    let isFired = false;
    const tick = () => {
      const left = Math.max(0, Math.ceil((endAt - Date.now()) / 1000));
      setRemainingSeconds(left);
      if (left === 0 && !isFired) {
        isFired = true;
        endProblemRef.current();
      }
    };
    tick();
    const timer = window.setInterval(tick, 250);
    return () => window.clearInterval(timer);
  }, [currentIndex, data.problem_remaining_seconds, data.section_id]);

  // 쓰는 중인 답을 잠깐씩 저장해 둔다. 창이 닫혀도 그 문항 시간이 다 되면 저장해 둔 답으로 매긴다
  const currentValue = problem ? valueById[problem.problem_id] : undefined;
  useEffect(() => {
    if (!problem || currentValue === undefined) return undefined;
    const timer = window.setTimeout(() => {
      if (isSendingRef.current) return;
      apiClient
        .post("/api/v1/assignment/math/section/submit", {
          section_id: data.section_id,
          student_id: identity.studentId,
          random_code: identity.randomCode,
          response_list: responseListOf(),
        }, { isSilent: true })
        .then((response) => {
          // 그사이 시간이 다 돼 서버가 다음 문항으로 넘겼으면 따라간다
          if (response.data.is_finished || response.data.current_problem_index !== currentIndexRef.current) onData(response.data);
        })
        .catch(() => {});
    }, LEVEL_TEST_AUTOSAVE_DELAY_MS);
    return () => window.clearTimeout(timer);
  }, [currentValue]); // eslint-disable-line react-hooks/exhaustive-deps

  // 시간표가 다 끝났는데 채점 결과가 아직 안 왔다(시각이 경계에 걸린 경우). 서버에 다시 묻는다
  useEffect(() => {
    if (currentIndex == null) reload();
  }, [currentIndex, reload]);

  if (!problem) return <div className="math-page__message">채점하는 중</div>;

  const limitSeconds = data.problem_time_limit_seconds || 1;
  const isHurry = remainingSeconds <= LEVEL_TEST_HURRY_SECONDS;
  const isLast = currentIndex === problemList.length - 1;
  const value = valueById[problem.problem_id] ?? "";
  // 빈 답도 묻지 않고 넘긴다(2026-10-22 대표: 못 푼 것은 오답)
  const pressNext = () => {
    if (isSending) return;
    endProblem();
  };
  const placeholderCount = countFigurePlaceholders(problem.question);
  const inlineImageList =
    placeholderCount && (problem.image_url_list || []).length >= placeholderCount
      ? problem.image_url_list.slice(0, placeholderCount)
      : [];

  return (
    <div className="math-test">
      <div className="math-test__head">
        <div className="math-test__head-row">
          <span className="math-test__dot" />
          <span className="math-test__title">{data.title}</span>
          <span className="math-test__progress">
            <b>{currentIndex + 1}</b> / {problemList.length}
          </span>
        </div>
        <div className="math-test__time-bar">
          <div
            className={`math-test__time-fill${isHurry ? " math-test__time-fill--hurry" : ""}`}
            style={{ width: `${Math.min(100, (remainingSeconds / limitSeconds) * 100)}%` }}
          />
        </div>
      </div>

      <div className="math-test__card">
        <div className="math-test__question-head">
          <span className="math-test__number">{problem.no || currentIndex + 1}</span>
          <div className="math-test__chips">
            {problem.unit_name && <span className="math-test__chip">{problem.unit_name}</span>}
            {problem.points != null && <span className="math-test__points">{problem.points}점</span>}
          </div>
          <span className={`math-test__clock${isHurry ? " math-test__clock--hurry" : ""}`} aria-label="이 문항 남은 시간">
            {testClockText(remainingSeconds)}
          </span>
        </div>
        <div className="math-test__question">
          <MathText text={problem.question} inlineImageList={inlineImageList} />
        </div>
        {(problem.image_url_list || []).slice(inlineImageList.length).map((url) => (
          <img key={url} className="math-test__image" src={url} alt="" />
        ))}
        <fieldset className="math-test__answer math-page__stage-body" disabled={isSending}>
          {problem.answer_type === "choice" && (
            <div className="math-test__choices">
              {problem.choice_list.map((choice, choiceIndex) => {
                const choiceNumber = String(choiceIndex + 1);
                const pickedList = Array.isArray(value) ? value.map(String) : [String(value)];
                const isPicked = pickedList.includes(choiceNumber);
                return (
                  <button
                    key={choiceNumber}
                    type="button"
                    aria-pressed={isPicked}
                    className={`math-test__choice${isPicked ? " math-test__choice--picked" : ""}`}
                    onClick={() =>
                      setValue(problem.problem_id)(
                        problem.multiple ? toggledList(Array.isArray(value) ? value.map(String) : [], choiceNumber) : choiceNumber
                      )
                    }
                  >
                    <span className="math-test__choice-number">{choiceIndex + 1}</span>
                    <MathText text={choice} />
                  </button>
                );
              })}
            </div>
          )}
          {problem.answer_type === "short" && (
            <ShortAnswer problem={problem} value={value} onChange={setValue(problem.problem_id)} disabled={isSending} />
          )}
          {problem.answer_type === "essay" && (
            <textarea
              className="math-page__essay"
              value={value}
              disabled={isSending}
              onChange={(event) => setValue(problem.problem_id)(event.target.value)}
            />
          )}
        </fieldset>
      </div>

      <div className="math-test__foot">
        <button type="button" className="math-test__next" disabled={isSending} onClick={pressNext}>
          {isSending ? "저장 중" : isLast ? "제출하기" : "다음 문항"}
          {!isSending && <span className="math-test__next-arrow">→</span>}
        </button>
      </div>
    </div>
  );
};

// 레벨테스트 시작 화면. '시작하기' 를 누르는 순간부터 시간이 흐른다
const MathLevelTestStart = ({ data, isStarting, onStart }) => (
  <div className="math-test math-test--start">
    <p className="math-test__eyebrow">ECHO MATH LEVEL TEST</p>
    <h1 className="math-test__start-title">{data.title}</h1>
    <div className="math-test__card">
      <div className="math-test__meta">
        <div>
          <i>문항 수</i>
          <b>{data.problem_count}문항</b>
        </div>
        <div>
          <i>시험 시간</i>
          <b>{data.time_limit_text}</b>
        </div>
        <div>
          <i>{data.total_points ? "만점" : "시간"}</i>
          <b>{data.total_points ? `${data.total_points}점` : data.is_per_problem_timed ? "문항마다" : "한 번에"}</b>
        </div>
      </div>
    </div>
    <div className="math-test__card">
      <ul className="math-test__rules">
        {data.is_per_problem_timed ? (
          <>
            <li>
              <b>문항마다 제한 시간</b>이 있어요. 시간이 끝나면 다음 문항으로 넘어가요.
            </li>
            <li>
              넘어간 문항으로는 <b>돌아갈 수 없어요.</b>
            </li>
          </>
        ) : (
          <li>
            시간이 다 되면 <b>그때까지 쓴 답으로</b> 채점돼요.
          </li>
        )}
      </ul>
      <button type="button" className="math-test__button" disabled={isStarting} onClick={onStart}>
        {isStarting ? "여는 중" : (
          <>
            시험 <em>시작하기</em>
          </>
        )}
      </button>
    </div>
  </div>
);

const MathSetPage = ({ setCurrentStep, isPreview = false }) => {
  const { section } = useSelector((state) => state.assignment);
  // 풀이 사진 창: null 이면 닫힘. { afterUpload } 는 올리거나 건너뛴 뒤 할 일(채점)
  const [photoAsk, setPhotoAsk] = useState(null);
  // 이번 화면에서 이미 올렸거나 건너뛰었는가. 상태로 두면 바로 이어 부르는 채점이 옛 값을 봐서 창이 또 뜬다
  const photoAnsweredRef = useRef(false);
  const isMobile = useIsMobile();
  const [data, setData] = useState(null);
  const [valueById, setValueById] = useState({});
  const [index, setIndex] = useState(0);
  const [tab, setTab] = useState("all");
  const [isJumpOpen, setIsJumpOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState("");
  const identity = useMemo(() => readSessionFromUrl(), []);
  const submitRef = useRef(() => {});

  const applyData = useCallback((next) => {
    setData(next);
    const saved = {};
    (next.response_list || []).forEach((one) => {
      saved[one.problem_id] = one.value;
    });
    setValueById(saved);
    const hasWrong = (next.response_list || []).some((one) => one.is_correct === false);
    setTab(next.is_finished && hasWrong ? "wrong" : "all");
  }, []);

  useEffect(() => {
    if (!section?.id || !identity) return;
    apiClient
      .get("/api/v1/assignment/math/section", {
        params: { section_id: section.id, student_id: identity.studentId, random_code: identity.randomCode },
      })
      .then((response) => applyData(response.data))
      .catch(() => setError("문제를 불러오지 못했어요."));
  }, [section?.id, identity, applyData]);

  const setValue = useCallback(
    (problemId) => (next) => setValueById((previous) => ({ ...previous, [problemId]: next })),
    []
  );

  // 시간이 다 되면 그때까지 쓴 답으로 바로 채점한다(묻지 않고, 풀이 사진도 묻지 않는다). 서버도 마감 뒤 저장은 받지 않는다
  const valueByIdRef = useRef(valueById);
  valueByIdRef.current = valueById;
  const remainingSeconds = useLevelTestClock(isPreview ? null : data, () => submitRef.current(true, valueByIdRef.current, true));
  const [isStarting, setIsStarting] = useState(false);
  const startTest = async () => {
    if (!identity || isStarting) return;
    setIsStarting(true);
    try {
      const response = await apiClient.post("/api/v1/assignment/math/section/start", {
        section_id: section.id,
        student_id: identity.studentId,
        random_code: identity.randomCode,
      });
      applyData(response.data);
    } catch (caught) {
      setError("시작하지 못했어요. 다시 눌러 주세요.");
    } finally {
      setIsStarting(false);
    }
  };

  const submit = async (isFinal, valueOverride = {}, isTimeUp = false) => {
    if (!identity || !data || data.is_finished || isPreview) return;
    setIsSaving(true);
    try {
      const response = await apiClient.post("/api/v1/assignment/math/section/submit", {
        section_id: data.section_id,
        student_id: identity.studentId,
        random_code: identity.randomCode,
        response_list: data.problem_list.map((problem) => ({
          problem_id: problem.problem_id,
          value: { ...valueById, ...valueOverride }[problem.problem_id] ?? "",
        })),
        is_final: isFinal,
      });
      if (isFinal || response.data.is_finished) {
        if (isTimeUp) window.alert("시험 시간이 끝나 지금까지 쓴 답으로 채점했어요.");
        applyData(response.data);
        setIndex(0);
        window.scrollTo(0, 0);
      }
    } catch (caught) {
      // 필수 풀이 사진을 안 올렸으면 서버가 막는다. 오류 대신 사진 창을 띄우고, 올리면 다시 낸다
      if (caught?.response?.data?.code === "work_photo_required") {
        photoAnsweredRef.current = false;
        setPhotoAsk({ afterUpload: () => submit(isFinal, valueOverride) });
        return;
      }
      setError("저장하지 못했어요. 다시 눌러 주세요.");
    } finally {
      setIsSaving(false);
    }
  };

  submitRef.current = submit;

  if (error) return <div className="math-page__message">{error}</div>;
  if (!data) return null;

  // 레벨테스트: '시작하기' 를 누르기 전에는 문제 대신 안내만 보인다. 누르는 순간부터 시간이 흐른다
  if (data.needs_start) {
    return <MathLevelTestStart data={data} isStarting={isStarting} onStart={startTest} />;
  }
  // 문항별 시간 레벨테스트를 푸는 중: 한 문항씩, 되돌아가기 없이. 선생님 미리보기와 채점 뒤는 아래 보통 화면
  if (data.is_per_problem_timed && !data.is_finished && !isPreview) {
    return <MathLevelTestRunner data={data} valueById={valueById} setValue={setValue} identity={identity} onData={applyData} />;
  }

  const problemList = data.problem_list;
  if (!problemList[index]) return <div className="math-page__message">문항이 없어요.</div>;
  const isFinished = data.is_finished;
  const keyById = Object.fromEntries((data.answer_key || []).map((one) => [one.problem_id, one]));
  const gradedById = Object.fromEntries((data.response_list || []).map((one) => [one.problem_id, one]));
  const wrongIndexList = problemList
    .map((one, problemIndex) => (gradedById[one.problem_id]?.is_correct === false ? problemIndex : -1))
    .filter((problemIndex) => problemIndex >= 0);
  // 연산 문제만 있는 세트는 한 화면에 5문제씩(2026-10-06 대표: 몇백 개를 하나씩 넘기면 못 푼다). 다섯 개 풀고 제출하면
  // 다음 다섯 개로. 그림이 있는 유형 문제는 그대로 한 문제씩
  const pageSize = problemList.every((one) => one.category === "computation") ? COMPUTATION_PAGE_SIZE : 1;
  const pageStart = Math.floor(index / pageSize) * pageSize;
  const pageProblemList = problemList.slice(pageStart, pageStart + pageSize);
  const isLast = pageStart + pageSize >= problemList.length;

  const moveTo = (nextIndex, valueOverride = {}) => {
    if (nextIndex < 0 || nextIndex >= problemList.length) return;
    if (!isFinished) submit(false, valueOverride);
    setIndex(nextIndex);
    setIsJumpOpen(false);
  };
  // 세트 채점. 안 푼 문제가 있으면 묻고, 그만두면 처음 안 푼 문제로 데려간다. 풀이 사진을 받는 과제면 그 전에 묻는다
  const finishSet = (valueOverride = {}) => {
    if (data.work_photo?.needed && !isPreview && !photoAnsweredRef.current) {
      setPhotoAsk({ afterUpload: () => finishSet(valueOverride) });
      return;
    }
    const merged = { ...valueById, ...valueOverride };
    const firstEmptyIndex = problemList.findIndex((one) => !isAnswered(merged[one.problem_id]));
    if (firstEmptyIndex >= 0) {
      const leftCount = problemList.filter((one) => !isAnswered(merged[one.problem_id])).length;
      if (!window.confirm(`${leftCount}문제를 안 풀었어요. 그래도 채점할까요?`)) {
        moveTo(firstEmptyIndex);
        return;
      }
    }
    submit(true, valueOverride);
  };
  // 이 화면(문제 하나 또는 다섯)을 내면 다음 화면으로, 마지막이면 세트 채점
  const submitProblem = (valueOverride = {}) => {
    if (isFinished || isSaving) return;
    if (isLast) finishSet(valueOverride);
    else moveTo(pageStart + pageSize, valueOverride);
  };

  const dotStateOf = (one) => {
    if (isFinished) {
      const judged = gradedById[one.problem_id]?.is_correct;
      return judged === true ? "right" : judged === false ? "wrong" : "none";
    }
    return isAnswered(valueById[one.problem_id]) ? "done" : "none";
  };

  const photoModal = photoAsk && (
    <WorkPhotoModal
      assignmentId={data.assignment_id}
      identity={identity}
      isRequired={data.work_photo?.policy === "required" || data.work_photo?.rating === "retry"}
      bonusPoints={data.work_photo?.bonus_points || 0}
      onClose={() => setPhotoAsk(null)}
      onSkip={() => {
        photoAnsweredRef.current = true;
        setPhotoAsk(null);
        photoAsk.afterUpload?.();
      }}
      onDone={(workPhoto) => {
        photoAnsweredRef.current = true;
        setData((previous) => ({ ...previous, work_photo: workPhoto }));
        setPhotoAsk(null);
        photoAsk.afterUpload?.();
      }}
    />
  );

  return (
    <div className="math-page">
      {photoModal}
      {isFinished && data.work_photo?.rating === "retry" && (
        <div className="math-page__photo-retry">
          선생님이 풀이 사진을 다시 올려 달래요
          <button type="button" className="math-page__submit" onClick={() => setPhotoAsk({ afterUpload: null })}>
            다시 올리기
          </button>
        </div>
      )}
      {isFinished && data.work_photo?.comment && (
        <div className="math-page__photo-comment">
          <span className="math-page__photo-comment-label">선생님 한마디</span>
          {data.work_photo.comment}
        </div>
      )}
      <div className="math-page__top">
        <div className="math-page__title">
          {data.title}
          {data.type_name && <span className="math-page__set">{data.type_name}</span>}
        </div>
        {isFinished && (
          <div className="math-page__score">
            {data.set_judged_count ? `${data.set_right_count} / ${data.set_judged_count} 정답` : "채점 완료"}
          </div>
        )}
        {!isFinished && remainingSeconds != null && (
          <div className={`math-page__clock${remainingSeconds <= 300 ? " math-page__clock--hurry" : ""}`}>
            남은 시간 {clockText(remainingSeconds)}
          </div>
        )}
        {!isFinished && isPreview && data.time_limit_text && (
          <div className="math-page__clock">제한 시간 {data.time_limit_text}</div>
        )}
      </div>

      {isFinished && (
        <div className="math-page__tabs" role="tablist">
          <button type="button" aria-selected={tab === "all"} onClick={() => setTab("all")}>
            전체 문제
          </button>
          <button type="button" aria-selected={tab === "wrong"} onClick={() => setTab("wrong")}>
            틀린 문제 {wrongIndexList.length}
          </button>
        </div>
      )}

      {isFinished && tab === "wrong" ? (
        <div className="math-page__wrong-list">
          <WrongPdfLinks assignmentId={data.assignment_id} identity={identity} hasWrong={wrongIndexList.length > 0} />
          <WorksheetDetailLink url={data.detail_explain_pdf_url} />
          {wrongIndexList.length === 0 && <div className="math-page__message">틀린 문제가 없어요.</div>}
          {wrongIndexList.map((problemIndex) => {
            const one = problemList[problemIndex];
            return (
              <ProblemCard
                key={one.problem_id}
                problem={one}
                number={one.no || problemIndex + 1}
                value={valueById[one.problem_id] ?? ""}
                onChange={() => {}}
                isFinished
                graded={gradedById[one.problem_id]}
                answerKey={keyById[one.problem_id]}
                footer={
                  <WrongProblemFooter
                    detailPdfUrl={keyById[one.problem_id]?.detail_pdf_url}
                    isReviewEnd={(data.review_end_problem_id_list || []).includes(one.problem_id)}
                  />
                }
              />
            );
          })}
        </div>
      ) : (
        <div className="math-page__stage">
          {!isMobile && (
            <ArrowButton direction="left" onClick={() => moveTo(pageStart - pageSize)} visibleCondition={pageStart > 0} />
          )}
          <div className="math-page__stage-body">
            {pageProblemList.map((problem, offset) => (
              <ProblemCard
                key={problem.problem_id}
                problem={problem}
                // 세트 2 부터도 학습지 원래 번호(31, 32…)로 보인다
                number={problem.no || pageStart + offset + 1}
                // 연산 다섯 문제 화면은 문제와 답칸을 한 줄에
                isCompact={pageSize > 1}
                value={valueById[problem.problem_id] ?? ""}
                onChange={setValue(problem.problem_id)}
                isFinished={isFinished}
                graded={gradedById[problem.problem_id]}
                answerKey={keyById[problem.problem_id]}
                // 답칸에서 Enter: 이 화면의 다음 칸으로, 마지막 칸이면 제출(MathField 가 다음 칸을 찾는다)
                onEnter={() => submitProblem()}
                // 한 문제씩 보는 객관식은 고르는 것이 곧 제출이다. 잠깐 보여 주고 넘어간다
                onPick={
                  pageSize === 1
                    ? (choiceNumber) =>
                        window.setTimeout(() => submitProblem({ [problem.problem_id]: choiceNumber }), 250)
                    : undefined
                }
                footer={
                  !isFinished &&
                  pageSize === 1 &&
                  problem.answer_type !== "choice" && (
                    // 주관식만(다섯 문제 화면은 맨 아래 하나). 답칸 바로 아래 오른쪽
                    <div className="math-page__submit-row">
                      <button
                        type="button"
                        className="math-page__submit"
                        disabled={isSaving || isPreview}
                        onClick={() => submitProblem()}
                      >
                        {isSaving ? "채점 중" : "제출하기"}
                      </button>
                    </div>
                  )
                }
              />
            ))}
            {/* 다섯 문제 화면은 마지막 카드 안이 아니라 카드들 아래에 */}
            {!isFinished && pageSize > 1 && (
              <div className="math-page__submit-row math-page__submit-row--page">
                <button type="button" className="math-page__submit" disabled={isSaving || isPreview} onClick={() => submitProblem()}>
                  {isSaving ? "채점 중" : "제출하기"}
                </button>
              </div>
            )}
          </div>
          {!isMobile && <ArrowButton direction="right" onClick={() => moveTo(pageStart + pageSize)} visibleCondition={!isLast} />}
        </div>
      )}

      {!(isFinished && tab === "wrong") && (
        <div className="math-page__bar">
          {isMobile && <ArrowButton direction="left" onClick={() => moveTo(pageStart - pageSize)} visibleCondition={pageStart > 0} />}
          <div className="math-page__jump">
            <button type="button" className="math-page__counter" onClick={() => setIsJumpOpen(!isJumpOpen)}>
              {pageSize > 1
                ? `${pageStart + 1}-${pageStart + pageProblemList.length}`
                : index + 1}{" "}
              / {problemList.length} <span className="math-page__caret">{isJumpOpen ? "▴" : "▾"}</span>
            </button>
            {isJumpOpen && <div className="math-page__jump-backdrop" onClick={() => setIsJumpOpen(false)} />}
            {isJumpOpen && (
              <div className="math-page__jump-grid">
                <div className="math-page__jump-head">
                  문제 이동
                  <button type="button" onClick={() => setIsJumpOpen(false)}>
                    ✕
                  </button>
                </div>
                {problemList.map((one, dotIndex) => (
                  <button
                    key={one.problem_id}
                    type="button"
                    className={`math-page__dot math-page__dot--${dotStateOf(one)}${dotIndex >= pageStart && dotIndex < pageStart + pageSize ? " math-page__dot--current" : ""}`}
                    onClick={() => moveTo(dotIndex)}
                  >
                    {one.no || dotIndex + 1}
                  </button>
                ))}
              </div>
            )}
          </div>
          {isMobile && <ArrowButton direction="right" onClick={() => moveTo(pageStart + pageSize)} visibleCondition={!isLast} />}
        </div>
      )}

      {isFinished && (
        <div className="math-page__leave">
          <button type="button" className="math-page__submit" onClick={() => setCurrentStep(1)}>
            나가기
          </button>
        </div>
      )}
    </div>
  );
};

// 빠른 정답 입력(2026-10-06): 종이에 푼 아이가 학습지 전체(모든 세트)의 답만 넣는다. 다 채점하면 같은 자리가 '틀린 문제'.
// 서버: api/assignment/math_assignment.py quick_payload · submit_quick
const MathQuickPage = ({ paragraphId, setCurrentStep, isPreview }) => {
  const identity = useMemo(() => readSessionFromUrl(), []);
  const [photoAsk, setPhotoAsk] = useState(null);
  const photoAnsweredRef = useRef(false);
  const [data, setData] = useState(null);
  const [valueById, setValueById] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState("");

  const applyData = useCallback((next) => {
    setData(next);
    setValueById(next.value_by_id || {});
  }, []);

  useEffect(() => {
    if (!identity || !paragraphId) return;
    apiClient
      .get("/api/v1/assignment/math/quick", {
        params: { paragraph_id: paragraphId, student_id: identity.studentId, random_code: identity.randomCode },
      })
      .then((response) => applyData(response.data))
      .catch(() => setError("불러오지 못했어요."));
  }, [identity, paragraphId, applyData]);

  const setValue = useCallback(
    (problemId) => (next) => setValueById((previous) => ({ ...previous, [problemId]: next })),
    []
  );

  const send = async (isFinal) => {
    if (!identity || !data || isPreview) return;
    setIsSaving(true);
    try {
      const response = await apiClient.post("/api/v1/assignment/math/quick", {
        paragraph_id: paragraphId,
        student_id: identity.studentId,
        random_code: identity.randomCode,
        response_list: data.problem_list
          .filter((one) => !one.is_finished)
          .map((one) => ({ problem_id: one.problem_id, value: valueById[one.problem_id] ?? "" })),
        is_final: isFinal,
      });
      applyData(response.data);
      if (isFinal) window.scrollTo(0, 0);
    } catch (caught) {
      if (caught?.response?.data?.code === "work_photo_required") {
        photoAnsweredRef.current = false;
        setPhotoAsk({ afterUpload: () => send(isFinal) });
        return;
      }
      setError("저장하지 못했어요. 다시 눌러 주세요.");
    } finally {
      setIsSaving(false);
    }
  };

  if (error) return <div className="math-page__message">{error}</div>;
  if (!data) return null;

  if (data.is_all_finished) {
    return (
      <div className="math-page">
        <div className="math-page__top">
          <div className="math-page__title">
            {data.title}
            <span className="math-page__set">틀린 문제 {data.wrong_list.length}</span>
          </div>
          <div className="math-page__score">
            {data.judged_count ? `${data.right_count} / ${data.judged_count} 정답` : "채점 완료"}
          </div>
        </div>
        <div className="math-page__wrong-list">
          <WrongPdfLinks assignmentId={data.assignment_id} identity={identity} hasWrong={data.wrong_list.length > 0} />
          <WorksheetDetailLink url={data.detail_explain_pdf_url} />
          {data.wrong_list.length === 0 && <div className="math-page__message">틀린 문제가 없어요.</div>}
          {data.wrong_list.map((one) => (
            <ProblemCard
              key={one.problem_id}
              problem={one}
              number={one.no}
              value={one.value ?? ""}
              onChange={() => {}}
              isFinished
              graded={one}
              answerKey={one.answer_key}
              footer={
                <WrongProblemFooter
                  detailPdfUrl={one.answer_key?.detail_pdf_url}
                  isReviewEnd={(data.review_end_problem_id_list || []).includes(one.problem_id)}
                />
              }
            />
          ))}
        </div>
        <div className="math-page__leave">
          <button type="button" className="math-page__submit" onClick={() => setCurrentStep(1)}>
            나가기
          </button>
        </div>
      </div>
    );
  }

  const openProblemList = data.problem_list.filter((one) => !one.is_finished);
  const leftCount = openProblemList.filter((one) => !isAnswered(valueById[one.problem_id])).length;
  const finish = () => {
    if (data.work_photo?.needed && !isPreview && !photoAnsweredRef.current) {
      setPhotoAsk({ afterUpload: finish });
      return;
    }
    if (!window.confirm(leftCount > 0 ? `${leftCount}문제를 안 적었어요. 그래도 채점할까요?` : "채점할까요?")) return;
    send(true);
  };
  const quickPhotoModal = photoAsk && (
    <WorkPhotoModal
      assignmentId={data.assignment_id}
      identity={identity}
      isRequired={data.work_photo?.policy === "required" || data.work_photo?.rating === "retry"}
      bonusPoints={data.work_photo?.bonus_points || 0}
      onClose={() => setPhotoAsk(null)}
      onSkip={() => {
        photoAnsweredRef.current = true;
        setPhotoAsk(null);
        photoAsk.afterUpload?.();
      }}
      onDone={(workPhoto) => {
        photoAnsweredRef.current = true;
        setData((previous) => ({ ...previous, work_photo: workPhoto }));
        setPhotoAsk(null);
        photoAsk.afterUpload?.();
      }}
    />
  );

  return (
    <div className="math-page math-page--quick">
      {quickPhotoModal}
      <div className="math-page__top">
        <div className="math-page__title">
          {data.title}
          <span className="math-page__set">빠른 정답 입력</span>
        </div>
      </div>
      <div className="math-quick__grid math-page__stage-body">
        {openProblemList.map((one) => (
          <div key={one.problem_id} className="math-quick__cell">
            <div className="math-quick__no">{one.no}번</div>
            {one.answer_type === "choice" ? (
              <div className="math-quick__choices">
                {Array.from({ length: one.choice_count || 5 }, (_, choiceIndex) => {
                  const choiceNumber = String(choiceIndex + 1);
                  const current = valueById[one.problem_id];
                  const pickedList = Array.isArray(current) ? current.map(String) : [String(current)];
                  return (
                    <button
                      key={choiceNumber}
                      type="button"
                      className={`math-quick__choice${pickedList.includes(choiceNumber) ? " math-quick__choice--picked" : ""}`}
                      onClick={() =>
                        setValue(one.problem_id)(
                          one.multiple
                            ? toggledList(Array.isArray(current) ? current.map(String) : [], choiceNumber)
                            : choiceNumber
                        )
                      }
                    >
                      {CIRCLED[choiceIndex]}
                    </button>
                  );
                })}
              </div>
            ) : (
              <ShortAnswer
                problem={one}
                value={valueById[one.problem_id] ?? ""}
                onChange={setValue(one.problem_id)}
                disabled={false}
                onEnter={finish}
              />
            )}
          </div>
        ))}
      </div>
      <div className="math-quick__actions">
        <button type="button" className="math-page__submit math-page__submit--ghost" disabled={isSaving || isPreview} onClick={() => send(false)}>
          저장
        </button>
        <button type="button" className="math-page__submit" disabled={isSaving || isPreview} onClick={finish}>
          {isSaving ? "채점 중" : "채점하기"}
        </button>
      </div>
    </div>
  );
};

const MathPage = (props) => {
  const { section } = useSelector((state) => state.assignment);
  if (section?.quickParagraphId) {
    return <MathQuickPage paragraphId={section.quickParagraphId} setCurrentStep={props.setCurrentStep} isPreview={props.isPreview} />;
  }
  return <MathSetPage {...props} />;
};

export default MathPage;
